import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';
import type { EventsSettings, EventsUrlPrefixes } from '@/types/events';

export type EventsSettingsInput = Omit<EventsSettings, 'id' | 'brand_id' | 'created_at' | 'updated_at'>;

const defaultUrlPrefixes: EventsUrlPrefixes = {
  events: 'events',
  event: 'events',
};

const defaultSettings: EventsSettingsInput = {
  site_url: null,
  url_prefixes: defaultUrlPrefixes,
};

export function useEventsSettings() {
  const { currentBrandId } = useBrandManager();
  const queryClient = useQueryClient();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['events-settings', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return null;
      const { data, error } = await supabase
        .from('os_events_settings')
        .select('*')
        .eq('brand_id', currentBrandId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return { ...defaultSettings, brand_id: currentBrandId, id: '' } as EventsSettings;
      return data as EventsSettings;
    },
    enabled: !!currentBrandId,
  });

  const saveMutation = useMutation({
    mutationFn: async (input: Partial<EventsSettingsInput>) => {
      if (!currentBrandId) throw new Error('No brand selected');
      const { data: user } = await supabase.auth.getUser();
      const payload = {
        brand_id: currentBrandId,
        user_id: user.user?.id ?? null,
        site_url: input.site_url,
        updated_at: new Date().toISOString(),
        url_prefixes: (input.url_prefixes !== undefined 
          ? input.url_prefixes 
          : settings?.id 
            ? (settings.url_prefixes ?? defaultUrlPrefixes)
            : defaultUrlPrefixes) as Json,
      };
      if (settings?.id) {
        const { data, error } = await supabase
          .from('os_events_settings')
          .update(payload)
          .eq('id', settings.id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('os_events_settings')
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events-settings', currentBrandId] });
      toast.success('Events settings saved');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to save events settings'),
  });

  return {
    settings,
    isLoading,
    error,
    save: saveMutation.mutate,
    saveAsync: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
    defaultSettings,
  };
}
